/**
 * The form model, mirroring `FormTemplate.kt` on the Android side.
 *
 * Both clients read the same template files out of the repo-root `templates/`
 * folder, so these types and the Kotlin data classes have to stay in step. When
 * one changes, change the other.
 */

export type Role = 'producer' | 'participant'

export type FieldType = 'text' | 'multiline' | 'email' | 'phone' | 'date' | 'choice'

export interface FormField {
  key: string
  label: string
  type: FieldType
  /** `producer` fields are filled in before the phone is handed over. */
  role: Role
  required: boolean
  /** Carry the value to the next signer. For what does not change across a shoot day. */
  remember: boolean
  hint: string
  default: string
  /**
   * For `choice` fields: the answers on offer, in order.
   *
   * Picking from a list beats typing when the same few answers recur, which on a
   * documentary they do. It also means the wording on a legal document is
   * written once, deliberately, rather than improvised at 04:30.
   */
  options: string[]
  /**
   * For `choice` fields: offer a free-text escape hatch as well.
   *
   * A fixed list keeps the wording deliberate, but a form that cannot describe
   * something unusual would push whoever is holding the phone into picking a
   * near-miss, and a near-miss on a legal document is worse than a sentence
   * typed on the spot.
   */
  allowOther: boolean
  /** Wording of the escape-hatch entry in the picker. */
  otherLabel: string
}

export interface SignatureSpec {
  key: string
  label: string
  required: boolean
}

export type Block =
  | { kind: 'heading'; level: number; runs: Run[] }
  | { kind: 'paragraph'; runs: Run[] }
  | { kind: 'bullet'; marker: string; runs: Run[] }
  | { kind: 'note'; runs: Run[] }
  | { kind: 'fieldLine'; key: string }
  | { kind: 'signature'; key: string }
  | { kind: 'rule' }

/**
 * A stretch of text with uniform styling. When `placeholder` is set, `text` is
 * the field key and the renderer substitutes the captured value.
 */
export interface Run {
  text: string
  bold: boolean
  italic: boolean
  placeholder?: string
}

export interface FormTemplate {
  id: string
  title: string
  version: number
  description: string
  /** Field whose value names the signer. Drives filenames and the release list. */
  nameFieldKey: string
  /** Field holding the signer's email, so they can be sent their own copy. */
  emailFieldKey: string | null
  fields: FormField[]
  signatures: SignatureSpec[]
  agreeText: string
  body: Block[]
}

export class TemplateFormatError extends Error {}

export function field(template: FormTemplate, key: string): FormField | undefined {
  return template.fields.find((f) => f.key === key)
}

export function fieldsFor(template: FormTemplate, role: Role): FormField[] {
  return template.fields.filter((f) => f.role === role)
}

/** Required fields with no value yet. */
export function missingRequired(
  template: FormTemplate,
  values: Record<string, string>,
): FormField[] {
  return template.fields.filter((f) => f.required && !(values[f.key] ?? '').trim())
}
